/**
 * Tenant financial-year configuration.
 *
 * Source of truth is the backend (`/api/financial-year`, stored on the Organization).
 * A tenant-scoped local cache keeps reads synchronous so date filters can resolve the
 * "Financial year" preset without awaiting the network. The cache is per tenant (not
 * per ledger) because the FY applies to every ledger and to consolidated reports.
 *
 * Changing the FY only changes which window reports and dashboards show — it never
 * modifies transactions, schedules or balances.
 */
import { useEffect, useState } from 'react';
import { financialYearApi, hasAuthToken, type TenantFinancialYear, type FinancialYearWindow } from '../api';
import { PREFIX } from './devStorage';
import { getTenantId } from './tenant';

export const FINANCIAL_YEAR_EVENT = 'financial-year';
export const DEFAULT_FY_START_MONTH = 4;

const CACHE_KEY = 'financial_year_v1';

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export type FinancialYearState = TenantFinancialYear & { canManage: boolean };

function cacheKey(): string {
  return `${PREFIX}${getTenantId() ?? 'anon'}_${CACHE_KEY}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function normalizeStartMonth(m: unknown): number {
  const n = Number(m);
  return Number.isInteger(n) && n >= 1 && n <= 12 ? n : DEFAULT_FY_START_MONTH;
}

export function fyLabel(startYear: number, startMonth: number): string {
  if (normalizeStartMonth(startMonth) === 1) return `FY ${startYear}`;
  return `FY ${startYear}-${pad2((startYear + 1) % 100)}`;
}

/** Inclusive FY window, e.g. (2026, 4) → 2026-04-01 … 2027-03-31. Mirrors backend lib/financialYear. */
export function fyWindow(startYear: number, startMonth: number): FinancialYearWindow {
  const m = normalizeStartMonth(startMonth);
  const endYear = m === 1 ? startYear : startYear + 1;
  const endMonth = m === 1 ? 12 : m - 1;
  const lastDay = new Date(endYear, endMonth, 0).getDate();
  return {
    startYear,
    label: fyLabel(startYear, m),
    startYmd: `${startYear}-${pad2(m)}-01`,
    endYmd: `${endYear}-${pad2(endMonth)}-${pad2(lastDay)}`,
  };
}

export function fyStartYearForDate(date: Date, startMonth: number): number {
  const m = normalizeStartMonth(startMonth);
  return date.getMonth() + 1 >= m ? date.getFullYear() : date.getFullYear() - 1;
}

function localTodayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Defaults used before the first hydrate (or offline): April start, current FY. */
export function defaultFinancialYear(now = new Date()): FinancialYearState {
  const current = fyWindow(fyStartYearForDate(now, DEFAULT_FY_START_MONTH), DEFAULT_FY_START_MONTH);
  return {
    startMonth: DEFAULT_FY_START_MONTH,
    selectedStartYear: null,
    current,
    selected: current,
    followsCurrent: true,
    updatedAt: null,
    updatedBy: '',
    canManage: false,
  };
}

/**
 * Re-derive windows locally so a cached "follow current year" setting rolls over on
 * 1st of the start month even if the app was left open.
 */
function normalize(input: unknown): FinancialYearState {
  const base = defaultFinancialYear();
  if (!input || typeof input !== 'object') return base;
  const p = input as Partial<FinancialYearState>;
  const startMonth = normalizeStartMonth(p.startMonth);
  const today = new Date(`${localTodayYmd()}T00:00:00`);
  const current = fyWindow(fyStartYearForDate(today, startMonth), startMonth);
  const selectedStartYear =
    typeof p.selectedStartYear === 'number' && Number.isInteger(p.selectedStartYear) ? p.selectedStartYear : null;
  const selected = selectedStartYear != null ? fyWindow(selectedStartYear, startMonth) : current;
  return {
    startMonth,
    selectedStartYear,
    current,
    selected,
    followsCurrent: selectedStartYear == null || selectedStartYear === current.startYear,
    updatedAt: p.updatedAt ?? null,
    updatedBy: p.updatedBy ?? '',
    canManage: Boolean(p.canManage),
  };
}

function emit() {
  try {
    window.dispatchEvent(new Event(FINANCIAL_YEAR_EVENT));
  } catch {
    /* no window */
  }
}

export function readFinancialYear(): FinancialYearState {
  try {
    const raw = localStorage.getItem(cacheKey());
    return normalize(raw ? JSON.parse(raw) : null);
  } catch {
    return defaultFinancialYear();
  }
}

function cacheFinancialYear(state: FinancialYearState): FinancialYearState {
  const next = normalize(state);
  try {
    localStorage.setItem(cacheKey(), JSON.stringify(next));
  } catch {
    /* quota or private mode */
  }
  emit();
  return next;
}

/** Selected FY as a Date range (local midnight start, end-of-day end). */
export function selectedFinancialYearRange(state = readFinancialYear()): { start: Date; end: Date } {
  const start = new Date(`${state.selected.startYmd}T00:00:00`);
  const end = new Date(`${state.selected.endYmd}T23:59:59.999`);
  return { start, end };
}

/** Pull the tenant FY from the backend into the local cache (on login / workspace hydrate). */
export async function hydrateFinancialYear(): Promise<FinancialYearState | null> {
  if (!hasAuthToken()) return null;
  try {
    const res = await financialYearApi.get();
    return cacheFinancialYear({ ...res.financialYear, canManage: res.canManage });
  } catch {
    return null;
  }
}

/**
 * Persist a change to the backend. Unlike branding, the FY is not saved locally first:
 * the server enforces who may change it, and a rejected change must not leave the
 * browser showing a year the rest of the team doesn't see.
 */
export async function saveFinancialYear(input: {
  startMonth?: number;
  selectedStartYear?: number | null;
}): Promise<FinancialYearState> {
  const res = await financialYearApi.update(input);
  return cacheFinancialYear({ ...res.financialYear, canManage: res.canManage });
}

export function useFinancialYear(): FinancialYearState {
  const [state, setState] = useState<FinancialYearState>(() => readFinancialYear());
  useEffect(() => {
    const fn = () => setState(readFinancialYear());
    window.addEventListener(FINANCIAL_YEAR_EVENT, fn);
    return () => window.removeEventListener(FINANCIAL_YEAR_EVENT, fn);
  }, []);
  return state;
}
